/* eslint-disable prefer-destructuring */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { AppRootState } from 'src/store/store'
import {
  CEActivity,
  TimelineData,
  AssignmentEvent,
  KPIData,
  ViewMode,
  TimeRange,
} from './types'

/**
 * Analytics dashboard state structure
 * Consolidates all useState hooks from the monolithic component
 */
export type AnalyticsState = {
  // Filter state
  selectedLinkedServiceID: string
  viewMode: ViewMode
  timeRange: TimeRange
  customStartDate: string | null // ISO string for Dayjs compatibility
  customEndDate: string | null // ISO string for Dayjs compatibility
  selectedUsers: string[]
  selectedCategories: string[]
  selectedLicenseTypes: string[]
  selectedGroups: string[]

  // Data state
  ceActivities: CEActivity[]
  timelineData: TimelineData[]
  assignmentEvents: AssignmentEvent[]
  kpiData: KPIData | null
  categories: string[]
  licenseTypes: string[]

  // UI state
  isProcessingData: boolean
  errorMessage: string
  alertMessage: string

  // Modal state
  selectedUserForDetails: string | null
  userDetailsOpen: boolean
  selectedDayData: TimelineData | null
  dayActivitiesOpen: boolean
  selectedAssignmentEvent: AssignmentEvent | null
  assignmentEventOpen: boolean

  // Download state
  downloading: string
  exportingCsv: boolean
}

export let initialState: AnalyticsState = {
  // Filters
  selectedLinkedServiceID: '',
  viewMode: 'timeline',
  timeRange: 'month',
  customStartDate: dayjs().subtract(1, 'month').toISOString(),
  customEndDate: dayjs().toISOString(),
  selectedUsers: [],
  selectedCategories: [],
  selectedLicenseTypes: [],
  selectedGroups: [],

  // Data
  ceActivities: [],
  timelineData: [],
  assignmentEvents: [],
  kpiData: null,
  categories: [],
  licenseTypes: [],

  // UI
  isProcessingData: false,
  errorMessage: '',
  alertMessage: '',

  // Modals
  selectedUserForDetails: null,
  userDetailsOpen: false,
  selectedDayData: null,
  dayActivitiesOpen: false,
  selectedAssignmentEvent: null,
  assignmentEventOpen: false,

  // Downloads
  downloading: '',
  exportingCsv: false,
}

/**
 * Async thunk for processing CE data
 * This will be called when filters change or data needs to be recomputed
 */
export let processCEData = createAsyncThunk(
  'analytics/processCEData',
  async (_, { getState, rejectWithValue }) => {
    try {
      let state = getState() as AppRootState
      let analytics = state.analytics
      let manage = state.manage

      if (!analytics.selectedLinkedServiceID) {
        return {
          ceActivities: [],
          timelineData: [],
          assignmentEvents: [],
          kpiData: null,
          categories: [],
          licenseTypes: [],
        }
      }

      // Import data processing utility
      let { processCEData: processData } =
        await import('./utils/data-processing')

      // Build parameters for data processing
      let params = {
        selectedLinkedServiceID: analytics.selectedLinkedServiceID,
        users: manage.users,
        recordedCertificates: manage.certificates,
        liveCertificates: manage.liveCertificates,
        externalCertificates: manage.externalCertificates,
        completedCourses: manage.completedCourses,
        assignments: manage.assignments,
        courses: manage.courses,
        licenses: manage.licenses,
        groups: manage.groups,

        // Filters
        selectedUsers: analytics.selectedUsers,
        selectedCategories: analytics.selectedCategories,
        selectedLicenseTypes: analytics.selectedLicenseTypes,
        selectedGroups: analytics.selectedGroups,
        timeRange: analytics.timeRange,
        customStartDate: analytics.customStartDate,
        customEndDate: analytics.customEndDate,
      }

      // Process data using pure functional utilities
      return processData(params)
    } catch (error: any) {
      return rejectWithValue(`Failed to process CE data: ${error.message}`)
    }
  },
)

let analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    // Filter actions
    setSelectedLinkedServiceID: (state, action: PayloadAction<string>) => {
      state.selectedLinkedServiceID = action.payload
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload
    },
    setTimeRange: (state, action: PayloadAction<TimeRange>) => {
      state.timeRange = action.payload
    },
    setCustomStartDate: (state, action: PayloadAction<string | null>) => {
      state.customStartDate = action.payload
    },
    setCustomEndDate: (state, action: PayloadAction<string | null>) => {
      state.customEndDate = action.payload
    },
    setSelectedUsers: (state, action: PayloadAction<string[]>) => {
      state.selectedUsers = action.payload
    },
    setSelectedCategories: (state, action: PayloadAction<string[]>) => {
      state.selectedCategories = action.payload
    },
    setSelectedLicenseTypes: (state, action: PayloadAction<string[]>) => {
      state.selectedLicenseTypes = action.payload
      // Clear groups when license types are selected (mutually exclusive)
      if (action.payload.length > 0 && state.selectedGroups.length > 0) {
        state.selectedGroups = []
        state.alertMessage =
          'Group filter was cleared because license types were selected'
      }
    },
    setSelectedGroups: (state, action: PayloadAction<string[]>) => {
      state.selectedGroups = action.payload
      // Clear license types when groups are selected (mutually exclusive)
      if (action.payload.length > 0 && state.selectedLicenseTypes.length > 0) {
        state.selectedLicenseTypes = []
        state.alertMessage =
          'License filter was cleared because a group was selected'
      }
    },

    // UI actions
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    setAlertMessage: (state, action: PayloadAction<string>) => {
      state.alertMessage = action.payload
    },
    clearMessages: (state) => {
      state.errorMessage = ''
      state.alertMessage = ''
    },

    // Modal actions
    setSelectedUserForDetails: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedUserForDetails = action.payload
    },
    setUserDetailsOpen: (state, action: PayloadAction<boolean>) => {
      state.userDetailsOpen = action.payload
      if (!action.payload) {
        state.selectedUserForDetails = null // Clear selection when closing
      }
    },
    setSelectedDayData: (state, action: PayloadAction<TimelineData | null>) => {
      state.selectedDayData = action.payload
    },
    setDayActivitiesOpen: (state, action: PayloadAction<boolean>) => {
      state.dayActivitiesOpen = action.payload
      if (!action.payload) {
        state.selectedDayData = null // Clear selection when closing
      }
    },
    setSelectedAssignmentEvent: (
      state,
      action: PayloadAction<AssignmentEvent | null>,
    ) => {
      state.selectedAssignmentEvent = action.payload
    },
    setAssignmentEventOpen: (state, action: PayloadAction<boolean>) => {
      state.assignmentEventOpen = action.payload
      if (!action.payload) {
        state.selectedAssignmentEvent = null // Clear selection when closing
      }
    },

    // Download actions
    setDownloading: (state, action: PayloadAction<string>) => {
      state.downloading = action.payload
    },
    setExportingCsv: (state, action: PayloadAction<boolean>) => {
      state.exportingCsv = action.payload
    },

    // Utility actions
    resetAllFilters: (state) => {
      state.selectedUsers = []
      state.selectedCategories = []
      state.selectedLicenseTypes = []
      state.selectedGroups = []
      state.timeRange = 'month'
      state.customStartDate = dayjs().subtract(1, 'month').toISOString()
      state.customEndDate = dayjs().toISOString()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processCEData.pending, (state) => {
        state.isProcessingData = true
        state.errorMessage = ''
      })
      .addCase(processCEData.fulfilled, (state, action) => {
        state.isProcessingData = false
        state.ceActivities = action.payload.ceActivities
        state.timelineData = action.payload.timelineData
        state.assignmentEvents = action.payload.assignmentEvents
        state.kpiData = action.payload.kpiData
        state.categories = action.payload.categories
        state.licenseTypes = action.payload.licenseTypes
      })
      .addCase(processCEData.rejected, (state, action) => {
        state.isProcessingData = false
        state.errorMessage =
          (action.payload as string) || 'Failed to process CE data'
      })
  },
})

export let {
  setSelectedLinkedServiceID,
  setViewMode,
  setTimeRange,
  setCustomStartDate,
  setCustomEndDate,
  setSelectedUsers,
  setSelectedCategories,
  setSelectedLicenseTypes,
  setSelectedGroups,
  setErrorMessage,
  setAlertMessage,
  clearMessages,
  setSelectedUserForDetails,
  setUserDetailsOpen,
  setSelectedDayData,
  setDayActivitiesOpen,
  setSelectedAssignmentEvent,
  setAssignmentEventOpen,
  setDownloading,
  setExportingCsv,
  resetAllFilters,
} = analyticsSlice.actions

export default analyticsSlice.reducer
